import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { Pill, fmtRelative } from '@/features/dossier/components/primitives'
import { listInbound, markHandled, type Inbound } from './api'

/**
 * What people sent back.
 *
 * ## Two different things arrive here and only one is machine-handled
 *
 * A STOP suppresses that address immediately, without a person in the loop, and is marked read
 * — the request has been honoured, and leaving it in the queue would bury the messages that
 * need a human under the ones that do not.
 *
 * Everything else is a person talking to the campaign. It is stored, linked to the message that
 * prompted it, and left unread until somebody opens it. Nothing here generates a reply: an
 * automated answer to a real question would be the campaign speaking without the campaign
 * deciding to.
 *
 * ## "Read" is not "answered"
 *
 * Marking a reply read is the smaller claim, and it is the only one this screen makes.
 * Conflating the two would hide the messages nobody actually answered.
 */
export function RepliesPage() {
  const [rows, setRows] = useState<Inbound[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [showHandled, setShowHandled] = useState(false)

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listInbound())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading replies" />

  const unread = rows.filter((r) => r.handledAt == null)
  const shown = showHandled ? rows : unread

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Replies"
        description="People answering a campaign. A STOP has already been honoured; everything
                     else is waiting for a person."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-2 p-2">
          <p className="text-[11px] text-muted-foreground">
            {unread.length} unread of {rows.length}.
          </p>
          <label className="flex items-center gap-1 text-[11px]">
            <input type="checkbox" checked={showHandled}
                   onChange={(e) => setShowHandled(e.target.checked)} />
            Show the ones already read
          </label>
        </div>
      </Panel>

      {shown.length === 0 && (
        <Panel>
          <p className="p-4 text-center text-[12px] text-muted-foreground">
            {showHandled
              ? 'Nothing has come back yet.'
              : 'Nothing unread. Every reply has been looked at.'}
          </p>
        </Panel>
      )}

      <ul className="space-y-2">
        {shown.map((r) => (
          <li key={r.id}>
            <Panel>
              <div className="space-y-1 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Pill className="border-border">{r.channelCode}</Pill>
                    {r.autoSuppressed && (
                      <Pill className="border-amber-500/30 bg-amber-500/10 text-amber-700
                                       dark:text-amber-400">
                        opted out — already honoured
                      </Pill>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      {fmtRelative(r.receivedAt)}
                    </span>
                  </div>
                  {r.handledAt == null && (
                    <button type="button" disabled={busy === r.id}
                            onClick={() => {
                              setBusy(r.id)
                              void markHandled(r.id)
                                .then(load)
                                .catch((err) => setError(explain(err)))
                                .finally(() => setBusy(null))
                            }}
                            className="rounded border border-border px-2 py-0.5 text-[10px]">
                      Mark read
                    </button>
                  )}
                </div>
                <p className="whitespace-pre-wrap rounded border border-border bg-muted/20 p-2
                              text-[12px]">{r.body}</p>
                {r.recipientId && (
                  <p className="text-[10px] text-muted-foreground">
                    A reply to a message this campaign sent.
                  </p>
                )}
                {r.handledAt && (
                  <p className="text-[10px] text-muted-foreground">
                    Read {fmtRelative(r.handledAt)}. Read, not answered.
                  </p>
                )}
              </div>
            </Panel>
          </li>
        ))}
      </ul>
    </div>
  )
}
